import { useState } from "react";
import {
  Alert, Box, Button, Checkbox, FormControlLabel, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { wardIssuesApi, type CreateWardIssueLineDto, type ItemDto } from "@/api/endpoints";
import { ItemPicker } from "@/components/Pickers";
import { BatchSelect } from "@/components/BatchSelect";
import { WarehousePicker } from "@/components/WarehousePicker";
import { errorMessage } from "@/api/client";
import { PatientSummaryByUhid } from "@/components/PatientSummary";
import { PatientUhidField } from "@/components/PatientUhidField";
import {
  LasaConfirmDialog, lasaConflictsFrom, type ServerLasaConflict,
} from "@/components/LasaConfirmDialog";
import { BinHint } from "@/components/BinHint";

/**
 * UC-FN-HOS-01 — Create ward issue.
 */

/** A cart line. `item` is local UI state; only its id goes to the API. */
interface DraftLine {
  key: string;
  item: ItemDto | null;
  batchId: string;
  qty: number;
  lineNotes: string;
}

const blankLine = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, batchId: "", qty: 1, lineNotes: "",
});

export function CreateWardIssuePage() {
  const navigate = useNavigate();
  const [uhid, setUhid] = useState("");
  const [lookupUhid, setLookupUhid] = useState(""); // committed UHID → patient preview
  const [admissionId, setAdmissionId] = useState("");
  const [wardCode, setWardCode] = useState("");
  const [wardWarehouseId, setWardWarehouseId] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);

  const usableLines = lines.filter(l => l.item && l.batchId && l.qty > 0);

  // Ward stock goes straight to a nurse for administration, so the confusable gate applies here
  // too. The server blocks the issue and returns the pairs; the operator confirms and we retry
  // carrying the acknowledgement, which is what gets audited.
  const [lasaConflicts, setLasaConflicts] = useState<ServerLasaConflict[] | null>(null);

  const mutation = useMutation({
    meta: { successMessage: "Ward issue created" },
    mutationFn: (lasaAcknowledgedItemIds?: string[]) => wardIssuesApi.create({
      uhid, admissionId: admissionId || undefined, wardCode: wardCode || undefined,
      wardWarehouseId, isEmergency, reason: reason || undefined,
      lines: usableLines.map((l): CreateWardIssueLineDto => ({
        itemId: l.item!.id, batchId: l.batchId, qty: l.qty, lineNotes: l.lineNotes || undefined,
      })),
      lasaAcknowledgedItemIds,
    }),
    onSuccess: () => navigate("/hospital/ward"),
    onError: (err) => setLasaConflicts(lasaConflictsFrom(err)),
  });

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(prev => prev.map(l => l.key === key ? { ...l, ...patch } : l));

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New ward issue</Typography>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <PatientUhidField value={uhid} required sx={{ flex: 1 }}
              onChange={setUhid} onCommit={setLookupUhid} />
            <TextField label="Admission ID" value={admissionId} size="small" sx={{ flex: 1 }}
              onChange={(e) => setAdmissionId(e.target.value)} />
            <TextField label="Ward" value={wardCode} size="small" sx={{ flex: 1 }}
              onChange={(e) => setWardCode(e.target.value)} />
          </Stack>
          {lookupUhid && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <PatientSummaryByUhid dense uhid={lookupUhid} />
            </Paper>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <WarehousePicker label="Ward warehouse" value={wardWarehouseId} required sx={{ flex: 1 }}
              // Batches are per warehouse, so changing it invalidates every line's batch.
              onChange={(v) => {
                setWardWarehouseId(v);
                setLines(prev => prev.map(l => ({ ...l, batchId: "" })));
              }} />
            <FormControlLabel control={
              <Checkbox checked={isEmergency} onChange={(e) => setIsEmergency(e.target.checked)} />
            } label="Emergency" />
          </Stack>
          <TextField label="Reason" value={reason} size="small" multiline minRows={2}
            onChange={(e) => setReason(e.target.value)} />

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Lines</Typography>
          {!wardWarehouseId && (
            <Alert severity="info">Pick a ward warehouse first — batches and bins are per warehouse.</Alert>
          )}
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 260 }}>Item *</TableCell>
                <TableCell sx={{ width: 240 }}>Batch · expiry *</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Qty *</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={l.key}>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <ItemPicker value={l.item} label={`Line ${i + 1}`} required error={!l.item}
                        // Changing the item invalidates the batch — it belonged to the old one.
                        onChange={(v) => update(l.key, { item: v, batchId: "" })} />
                      <BinHint itemId={l.item?.id} warehouseId={wardWarehouseId} label="Pick from" />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <BatchSelect
                      itemId={l.item?.id} warehouseId={wardWarehouseId}
                      value={l.batchId} qty={l.qty}
                      onChange={(batchId) => update(l.key, { batchId })}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" sx={{ width: 80 }} value={l.qty}
                      inputProps={{ min: 0, step: "any", style: { textAlign: "right" } }}
                      onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })} />
                  </TableCell>
                  <TableCell><TextField size="small" fullWidth value={l.lineNotes}
                    onChange={(e) => update(l.key, { lineNotes: e.target.value })} /></TableCell>
                  <TableCell>
                    <IconButton size="small" disabled={lines.length === 1}
                      onClick={() => setLines(prev => prev.filter(x => x.key !== l.key))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(prev => [...prev, blankLine()])}>
            Add line
          </Button>

          {/* The LASA block is shown by its own dialog, not as a red banner behind it. */}
          {mutation.isError && !lasaConflicts && (
            <Alert severity="error">{errorMessage(mutation.error)}</Alert>
          )}
          {(!uhid || !wardWarehouseId || usableLines.length === 0) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {!uhid ? "UHID is required."
                : !wardWarehouseId ? "Pick a ward warehouse."
                : "Add at least one line with an item, batch and quantity."}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => navigate("/hospital/ward")}>Cancel</Button>
            <Button variant="contained" onClick={() => mutation.mutate(undefined)}
              disabled={!uhid || !wardWarehouseId || usableLines.length === 0 || mutation.isPending}>
              Save draft
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <LasaConfirmDialog
        conflicts={lasaConflicts}
        busy={mutation.isPending}
        onCancel={() => setLasaConflicts(null)}
        onAccept={(itemIds) => { setLasaConflicts(null); mutation.mutate(itemIds); }}
      />
    </Box>
  );
}
